import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription, merge, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { CurrentAthleteService } from '../current-athlete.service';
import { FeatureGateService } from '../../auth/feature-gate.service';
import { bottomSheetDialogConfig } from '../bottom-sheet-dialog.config';
import { FutureSession, FutureSessionService } from './future-session.service';
import { FutureSessionDialogComponent } from './future-session-dialog.component';

/**
 * Globaler Hinweis „Einheiten in der Zukunft“ (falsche Geräteuhr in der
 * FIT-Datei). Aufbau wie die Zonen-/DMM-Hinweise: der Sportler sieht seine
 * eigenen Einheiten, ein Coach ohne Athletenwahl die Sammelliste aller
 * betreuten Sportler (das Backend liefert dann `athlete_name` mit).
 *
 * Wegklicken gilt bis zum nächsten Laden — erledigt ist der Hinweis erst,
 * wenn das Datum korrigiert oder die Einheit gelöscht ist.
 */
@Component({
  selector: 'app-future-session-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslateModule],
  template: `
    @if (!suppressed && sessions.length) {
      <div class="fs-banner" role="status">
        <mat-icon class="fs-icon">event_busy</mat-icon>
        <div class="fs-text">
          <strong>
            {{ (sessions.length === 1
                  ? 'training_analysis.future_sessions.banner.title_one'
                  : 'training_analysis.future_sessions.banner.title_many')
                | translate:{ count: sessions.length } }}
          </strong>
          <span class="fs-body">
            {{ 'training_analysis.future_sessions.banner.body' | translate:{ dates: datesText } }}
          </span>
        </div>
        <span class="grow"></span>
        <button mat-flat-button color="primary" (click)="open()">
          {{ 'training_analysis.future_sessions.banner.cta' | translate }}
        </button>
        <button mat-icon-button (click)="dismiss()" aria-label="dismiss">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    }
  `,
  styles: [`
    .fs-banner {
      display: flex; align-items: flex-start; gap: 0.6rem;
      padding: 0.6rem 0.85rem; margin: 0.5rem 0;
      background: var(--status-warning-bg, #fff4e5);
      color: var(--status-warning-text, #92400e);
      border-left: 4px solid var(--color-warning, #d97706);
      border-radius: 6px; font-size: var(--fs-sm);
    }
    .fs-icon { color: var(--color-warning, #d97706); flex-shrink: 0; margin-top: 0.1rem; }
    .fs-text { flex: 1; min-width: 0; }
    .fs-text strong { display: block; }
    .fs-body { display: block; margin-top: 0.15rem; }
    .grow { flex: 1; }
  `],
})
export class FutureSessionBannerComponent implements OnInit, OnDestroy {
  private readonly svc = inject(FutureSessionService);
  private readonly dialog = inject(MatDialog);
  private readonly currentAthlete = inject(CurrentAthleteService);
  private readonly featureGate = inject(FeatureGateService);
  private readonly cdr = inject(ChangeDetectorRef);

  static readonly FEATURE = 'training.future_dated_sessions';

  sessions: FutureSession[] = [];
  suppressed = false;
  private sub?: Subscription;

  /** Bis zu drei Daten im Klartext, z.B. „01.01.2075, 10.03.2095“. */
  get datesText(): string {
    const fmt = (iso: string) => iso.split('-').reverse().join('.');
    const dates = this.sessions.map((s) => fmt(s.date));
    return dates.length > 3 ? `${dates.slice(0, 3).join(', ')} …` : dates.join(', ');
  }

  ngOnInit(): void {
    // Neu laden bei Athletenwechsel (dann auch das Wegklicken zurücksetzen) UND
    // nach jeder Korrektur/Löschung aus dem Dialog.
    this.sub = merge(
      this.currentAthlete.athleteId$.pipe(tap(() => { this.suppressed = false; })),
      this.svc.changed$,
    )
      .pipe(switchMap(() => this.load()))
      .subscribe((sessions) => {
        this.sessions = sessions ?? [];
        this.cdr.markForCheck();
      });
  }

  private load(): Observable<FutureSession[]> {
    // Ohne gewählten Athleten nicht fragen (403-Rauschen vor Login/Auswahl).
    if (this.currentAthlete.snapshot() == null
        || !this.featureGate.canAccess(FutureSessionBannerComponent.FEATURE)) {
      return of([]);
    }
    return this.svc.list().pipe(catchError(() => of([] as FutureSession[])));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  open(): void {
    this.dialog.open(FutureSessionDialogComponent, {
      ...bottomSheetDialogConfig('640px'),
      data: { sessions: this.sessions },
      autoFocus: false,
    });
  }

  dismiss(): void {
    this.suppressed = true;
    this.cdr.markForCheck();
  }
}
